export const COMPACT_NUMBER_THRESHOLD = 1_000_000;

export type CompactNumberResult = {
  display: string;
  full: string;
  isCompact: boolean;
};

export type FormatNumberOptions = Intl.NumberFormatOptions & {
  locale?: string;
};

const DEFAULT_LOCALE = "lo-LA";

export function formatFullNumber(
  value: number,
  options: FormatNumberOptions = {},
): string {
  const { locale = DEFAULT_LOCALE, ...intlOptions } = options;
  return value.toLocaleString(locale, intlOptions);
}

/** LAK amounts — same as inventory lots table (`ກີບ`, locale lo-LA). */
export function formatLakDisplay(lak: number | null | undefined): string {
  if (lak == null || Number.isNaN(lak)) return "–";
  return `${new Intl.NumberFormat(DEFAULT_LOCALE).format(lak)} ກີບ`;
}

/** Quantities in base unit (g, ml, pcs). */
export function formatQtyDisplay(qty: number): string {
  return Number(qty).toLocaleString(DEFAULT_LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatScaled(value: number, suffix: string): string {
  if (value >= 100) {
    return `${Math.round(value)}${suffix}`;
  }

  return `${value.toFixed(1).replace(/\.0$/, "")}${suffix}`;
}

/**
 * Compact numbers above 999,999 with K/M/B-style suffixes.
 * Returns full formatted value for tooltips.
 */
export function formatCompactNumber(
  value: number,
  options: FormatNumberOptions = {},
): CompactNumberResult {
  const full = formatFullNumber(value, options);
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (abs < COMPACT_NUMBER_THRESHOLD) {
    return { display: full, full, isCompact: false };
  }

  let display: string;
  if (abs >= 1_000_000_000) {
    display = `${sign}${formatScaled(abs / 1_000_000_000, "B")}`;
  } else {
    const millions = abs / 1_000_000;
    if (millions >= 999.5) {
      display = `${sign}${formatScaled(abs / 1_000_000_000, "B")}`;
    } else {
      display = `${sign}${formatScaled(millions, "M")}`;
    }
  }

  return { display, full, isCompact: true };
}
